/**
 * Session state: who is signed in, what they may reach, and keeping the token alive.
 *
 * A session is two calls, not one. `/auth/login` and `/auth/refresh` mint an
 * access token; `/auth/me` says what that user can see. Keeping them separate is
 * what lets a role change or a team reassignment take effect without a new
 * token, which in the predecessor required signing out and back in because the
 * role was baked into a token honoured for an hour.
 */

import { create } from 'zustand';

import * as authApi from '@/api/auth';
import type { Identity, TokenGrant } from '@/api/auth';
import { setTokenProvider, setUnauthorizedHandler } from '@/api/client';
import { ROLE_RANK, type DroneId, type Role, type Team, type User } from '@/types/domain';

export type AuthStatus = 'unknown' | 'anonymous' | 'authenticated';

/**
 * The CSRF token survives a page reload in sessionStorage.
 *
 * It is not a credential on its own: the refresh cookie is httpOnly and this
 * value is useless without it. Holding it only in memory would mean every
 * reload signed the operator out, and an operator who has just watched a laptop
 * reboot in the field should not also have to find their password.
 */
const CSRF_KEY = 'drone-c2.csrf';

function readCsrf(): string | null {
  try {
    return window.sessionStorage.getItem(CSRF_KEY);
  } catch {
    return null;
  }
}

function writeCsrf(value: string | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(CSRF_KEY);
    else window.sessionStorage.setItem(CSRF_KEY, value);
  } catch {
    /* private browsing denies storage; the session then lasts until reload */
  }
}

interface AuthStoreState {
  status: AuthStatus;
  user: User | null;
  teams: Team[];
  token: string | null;
  error: string | null;
  busy: boolean;

  /** Null means the role sees every drone, so there is no list to check against. */
  accessibleDroneIds: DroneId[] | null;
  controllableDroneIds: DroneId[] | null;
  seesAllDrones: boolean;

  bootstrap: () => Promise<void>;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refresh: () => Promise<boolean>;
  /** Re-read the drone scope after an `access_changed` frame. */
  reloadIdentity: () => Promise<void>;
  clearError: () => void;
}

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let inFlightRefresh: Promise<boolean> | null = null;

function cancelRefresh(): void {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

/**
 * Renew at 80 percent of the token's life, floored at 30 seconds.
 *
 * The predecessor renewed at expiry minus a fixed 60 seconds, so a 90-second
 * test token renewed every 30 seconds and a clock two minutes out of sync never
 * renewed at all. A fraction of the server-declared lifetime has neither
 * failure mode.
 */
function scheduleRefresh(expiresInSeconds: number, run: () => void): void {
  cancelRefresh();
  const delayMs = Math.max(30_000, expiresInSeconds * 1000 * 0.8);
  refreshTimer = setTimeout(run, delayMs);
}

export const useAuthStore = create<AuthStoreState>((set, get) => {
  function applyIdentity(identity: Identity): void {
    set({
      user: identity.user,
      teams: identity.teams ?? [],
      seesAllDrones: identity.seesAllDrones,
      accessibleDroneIds: identity.accessibleDroneIds,
      controllableDroneIds: identity.controllableDroneIds,
    });
  }

  async function adopt(grant: TokenGrant): Promise<void> {
    writeCsrf(grant.csrfToken);
    set({
      status: 'authenticated',
      user: grant.user,
      token: grant.accessToken,
      error: null,
      busy: false,
    });
    scheduleRefresh(grant.expiresIn, () => {
      void get().refresh();
    });

    const identity = await authApi.me();
    if (identity.ok && identity.data) applyIdentity(identity.data);
  }

  function clear(error: string | null = null): void {
    cancelRefresh();
    writeCsrf(null);
    set({
      status: 'anonymous',
      user: null,
      teams: [],
      token: null,
      error,
      busy: false,
      accessibleDroneIds: [],
      controllableDroneIds: [],
      seesAllDrones: false,
    });
  }

  return {
    status: 'unknown',
    user: null,
    teams: [],
    token: null,
    error: null,
    busy: false,
    accessibleDroneIds: [],
    controllableDroneIds: [],
    seesAllDrones: false,

    bootstrap: async () => {
      // A reload has the refresh cookie but no access token, so the session is
      // re-established through refresh rather than /auth/me, which needs a
      // bearer token it does not have yet. It goes through the shared refresh:
      // StrictMode mounts App's effect twice in development, and two refreshes
      // presenting one cookie read as a replay to the server, which revokes the
      // whole family. That was the sign-out on a dev reload.
      await get().refresh();
    },

    login: async (username, password) => {
      set({ busy: true, error: null });
      const result = await authApi.login({ username, password });
      if (!result.ok) {
        // The server's own wording, not a status code. "Invalid username or
        // password" tells an operator what to do next; "HTTP 401" does not.
        clear(result.error.message);
        return false;
      }
      await adopt(result.data);
      return true;
    },

    logout: async () => {
      cancelRefresh();
      await authApi.logout();
      clear();
    },

    refresh: async () => {
      // Several 401s can land in the same tick when a page issues parallel
      // requests. They must share one refresh: the server rotates the refresh
      // cookie on every use, so a second concurrent call would present a token
      // the first had already retired and end the session outright.
      if (inFlightRefresh) return inFlightRefresh;

      inFlightRefresh = (async () => {
        const csrf = readCsrf();
        if (!csrf) {
          clear();
          return false;
        }
        const result = await authApi.refresh(csrf);
        if (result.ok && result.data) {
          await adopt(result.data);
          return true;
        }
        clear(get().status === 'authenticated' ? 'Session expired. Sign in again.' : null);
        return false;
      })();

      try {
        return await inFlightRefresh;
      } finally {
        inFlightRefresh = null;
      }
    },

    reloadIdentity: async () => {
      const identity = await authApi.me();
      if (identity.ok && identity.data) applyIdentity(identity.data);
    },

    clearError: () => set({ error: null }),
  };
});

// The client asks for the token rather than importing this store, which keeps
// api/client.ts free of store imports and testable on its own.
setTokenProvider(() => useAuthStore.getState().token);
setUnauthorizedHandler(async () => {
  if (useAuthStore.getState().status !== 'authenticated') return false;
  return useAuthStore.getState().refresh();
});

export function useAuthStatus(): AuthStatus {
  return useAuthStore((state) => state.status);
}

export function useCurrentUser(): User | null {
  return useAuthStore((state) => state.user);
}

export function useTeams(): Team[] {
  return useAuthStore((state) => state.teams);
}

export function useRole(): Role | null {
  return useAuthStore((state) => state.user?.role ?? null);
}

/** True when the current role is at or above `minimum`. */
export function useHasRole(minimum: Role): boolean {
  return useAuthStore((state) => {
    const role = state.user?.role;
    return role !== undefined && ROLE_RANK[role] >= ROLE_RANK[minimum];
  });
}

/**
 * Whether this session may command a specific aircraft.
 *
 * Read it as "operator or better, and this drone is in scope". The predecessor
 * wrote its checks as `if (user && !canControl(...))`, which skipped the check
 * entirely for an anonymous caller. Absence of a role is denial here, never a
 * pass, and the server checks again regardless: this only decides whether a
 * button is offered.
 */
export function useCanControl(droneId: DroneId): boolean {
  return useAuthStore((state) => {
    const role = state.user?.role;
    if (role === undefined || ROLE_RANK[role] < ROLE_RANK.operator) return false;
    return state.controllableDroneIds === null || state.controllableDroneIds.includes(droneId);
  });
}

export function useCanCommand(): boolean {
  return useHasRole('operator');
}

/**
 * Whether the Admin screen opens. From Group Commander up: a Group Commander
 * runs its own teams there, and the server scopes every list it is handed, so
 * this only decides whether the screen is offered.
 */
export function useCanAdminister(): boolean {
  return useHasRole('group_commander');
}

export function hasRole(minimum: Role): boolean {
  const role = useAuthStore.getState().user?.role;
  return role !== undefined && ROLE_RANK[role] >= ROLE_RANK[minimum];
}

export function getAccessToken(): string | null {
  return useAuthStore.getState().token;
}
